import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/axios';
import { resolveMediaUrl } from '@/lib/assetUrl';
import { useLocale } from '@/context/LocaleContext';
import type { ChatMessage } from '@/hooks/useMessages';
import { useFormatMessageTime } from '@/pages/messages/messageUtils';

interface MessageThreadProps {
  messages: ChatMessage[];
  currentCharId: number;
  isLoading: boolean;
  loadError?: boolean;
  onRetry?: () => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
}

function TranslateIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 24 24" fill="none">
      <path
        d="M5 8h6m-2 0V6c0-.6.4-1 1-1h10c.6 0 1 .4 1 1v8c0 .6-.4 1-1 1h-2M5 8v8c0 .6.4 1 1 1h8c.6 0 1-.4 1-1v-8c0-.6-.4-1-1-1H6c-.6 0-1 .4-1 1z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="m9 15 3-3m0 0 3-3m-3 3 3 3m-3-3-3 3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RestoreIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none">
      <path
        d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M21 3v5h-5M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3 21v-5h5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function MessageThread({
  messages,
  currentCharId,
  isLoading,
  loadError,
  onRetry,
  onLoadMore,
  hasMore,
  isLoadingMore,
}: MessageThreadProps) {
  const { t, locale } = useLocale();
  const formatTime = useFormatMessageTime();
  const areaRef = useRef<HTMLDivElement>(null);
  const scrollHeightBeforeLoadRef = useRef(0);
  const prevLastMessageIdRef = useRef<number | null>(null);
  const [translatedText, setTranslatedText] = useState<Record<number, string>>({});
  const [showTranslation, setShowTranslation] = useState<Record<number, boolean>>({});
  const [translating, setTranslating] = useState<Record<number, boolean>>({});

  // CI4: scroll al fondo solo en carga inicial o mensaje nuevo; al prepend conservar posición
  useEffect(() => {
    const el = areaRef.current;
    if (!el || isLoading || isLoadingMore) return;

    if (scrollHeightBeforeLoadRef.current > 0) {
      el.scrollTop = el.scrollHeight - scrollHeightBeforeLoadRef.current;
      scrollHeightBeforeLoadRef.current = 0;
      prevLastMessageIdRef.current = messages[messages.length - 1]?.id ?? null;
      return;
    }

    const lastId = messages[messages.length - 1]?.id ?? null;
    if (messages.length > 0 && lastId !== prevLastMessageIdRef.current) {
      el.scrollTop = el.scrollHeight;
    }
    prevLastMessageIdRef.current = lastId;
  }, [isLoading, isLoadingMore, messages]);

  const handleScroll = useCallback(() => {
    const el = areaRef.current;
    if (!el || el.scrollTop > 8 || !hasMore || isLoadingMore) return;
    scrollHeightBeforeLoadRef.current = el.scrollHeight;
    onLoadMore?.();
  }, [hasMore, isLoadingMore, onLoadMore]);

  const toggleTranslate = async (msg: ChatMessage) => {
    const originalText = msg.message.trim();
    if (!originalText) return;

    if (showTranslation[msg.id]) {
      setShowTranslation((prev) => ({ ...prev, [msg.id]: false }));
      return;
    }

    if (translatedText[msg.id]) {
      setShowTranslation((prev) => ({ ...prev, [msg.id]: true }));
      return;
    }

    setTranslating((prev) => ({ ...prev, [msg.id]: true }));
    try {
      // Paridad CI4: POST /api/v1/translate { text } + Accept-Language del locale UI
      const { data } = await api.post<{
        success: boolean;
        data?: { translation: string };
        message?: string;
      }>('/api/v1/translate', { text: originalText, target_lang: locale });

      if (data.success && data.data?.translation) {
        setTranslatedText((prev) => ({ ...prev, [msg.id]: data.data!.translation }));
        setShowTranslation((prev) => ({ ...prev, [msg.id]: true }));
      } else {
        window.alert(t('messages_translate_error'));
      }
    } catch {
      window.alert(t('messages_translate_error'));
    } finally {
      setTranslating((prev) => ({ ...prev, [msg.id]: false }));
    }
  };

  if (isLoading) {
    return (
      <div className="messages-area" id="messages-area">
        <div className="loading-messages">{t('messages_loading')}</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="messages-area" id="messages-area">
        <div className="no-messages">
          <p>{t('messages_error_load')}</p>
          {onRetry ? (
            <button type="button" className="btn-new-chat" onClick={onRetry}>
              {t('messages_retry')}
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="messages-area" id="messages-area">
        <div className="no-messages">{t('messages_no_messages')}</div>
      </div>
    );
  }

  return (
    <div className="messages-area" id="messages-area" ref={areaRef} onScroll={handleScroll}>
      <div className="messages-thread-inner">
        {isLoadingMore ? <div className="loading-messages">{t('messages_loading')}</div> : null}
        {hasMore && !isLoadingMore ? (
          <div className="load-older-hint">{t('messages_scroll_for_more')}</div>
        ) : null}
        {messages.map((msg) => {
          const isSent = Number(msg.sender_CharID) === currentCharId;
          const bubbleClass = isSent ? 'sent' : 'received';
          const isShowingTranslation = Boolean(showTranslation[msg.id]);
          const displayText = isShowingTranslation ? (translatedText[msg.id] ?? msg.message) : msg.message;
          const itemIcon = msg.itemIcon ? resolveMediaUrl(msg.itemIcon) : '/assets/images/icon/icon_default.webp';
          const isLocked = msg.isLocked === 1;
          const isTranslating = Boolean(translating[msg.id]);
          const canTranslate = !isSent && msg.message.trim().length > 0;

          return (
            <div key={msg.id} className={`message-bubble ${bubbleClass}`} data-msg-id={msg.id}>
              <div className="message-content">
                {msg.message.trim() ? (
                  <p className="message-text" data-original={msg.message}>
                    {displayText}
                  </p>
                ) : null}
                {msg.id64 ? (
                  <div className="attached-item item-with-tooltip" data-id64={msg.id64}>
                    <div className="attached-item-icon-wrapper">
                      <img
                        src={itemIcon}
                        alt=""
                        className="attached-item-icon-img"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/assets/images/icon/icon_default.webp';
                        }}
                      />
                      {isLocked ? <div className="inventory-item-lock-icon" /> : null}
                    </div>
                    <span className="attached-item-name-label">
                      {msg.itemName ?? t('messages_item_attached')}
                    </span>
                  </div>
                ) : null}
                <div className="message-footer">
                  {canTranslate ? (
                    <button
                      type="button"
                      className={`translate-msg-btn${isShowingTranslation ? ' translated' : ''}`}
                      title={isShowingTranslation ? t('messages_show_original') : t('messages_translate')}
                      disabled={isTranslating}
                      onClick={() => void toggleTranslate(msg)}
                    >
                      {isTranslating ? (
                        <div className="translate-spinner" />
                      ) : isShowingTranslation ? (
                        <RestoreIcon />
                      ) : (
                        <TranslateIcon />
                      )}
                    </button>
                  ) : null}
                  <span className="message-time">{formatTime(msg.created_at)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
